// ============================================================
// Watchlist — Top-100∩Perps Sidebar mit Live-Preisen + Suche.
// Klick lädt das Symbol in den Hauptchart.
// ============================================================
import React, { useMemo, useState } from 'react';
import { UniverseItem, fmtPrice } from './types';

interface Props {
  universe: UniverseItem[];
  tickers: Record<string, { last: number; changePct: number }>;
  selected: string;
  onSelect: (symbol: string) => void;
  status: string;
  // Drawer-Modus (Phone): als Off-Canvas-Panel mit Overlay rendern.
  asDrawer?: boolean;
  open?: boolean;
  onClose?: () => void;
  // Tablet: schmaler.
  width?: number;
}

const Watchlist: React.FC<Props> = ({ universe, tickers, selected, onSelect, status, asDrawer = false, open = false, onClose, width = 270 }) => {
  const [q, setQ] = useState('');

  const rows = useMemo(() => {
    const f = q.trim().toUpperCase();
    return universe.filter(u => !f || u.symbol.includes(f) || u.base.includes(f) || u.name.toUpperCase().includes(f));
  }, [universe, q]);

  const dot = status === 'open' ? '#22c55e' : status === 'connecting' ? '#f59e0b' : '#ef4444';

  // Symbol wählen → im Drawer-Modus schließt das Panel.
  const pick = (symbol: string) => { onSelect(symbol); if (asDrawer) onClose?.(); };

  // Container-Style: Desktop/Tablet = Spalte im Flow; Phone = Off-Canvas-Drawer.
  const panelStyle: React.CSSProperties = asDrawer
    ? {
        position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 60,
        width: 'min(86vw, 320px)',
        paddingTop: 'env(safe-area-inset-top)', paddingLeft: 'env(safe-area-inset-left)',
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform .25s ease',
        boxShadow: open ? '4px 0 24px rgba(0,0,0,.6)' : 'none',
        display: 'flex', flexDirection: 'column', background: '#0c1120', borderRight: '1px solid #1e2539',
      }
    : { width, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#0c1120', borderRight: '1px solid #1e2539', height: '100%' };

  return (
    <>
      {asDrawer && (
        <div onClick={onClose} style={{
          position: 'fixed', inset: 0, zIndex: 59, background: 'rgba(0,0,0,.5)',
          opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none', transition: 'opacity .25s ease',
        }} />
      )}
      <div style={panelStyle}>
      <div style={{ padding: '10px 12px', borderBottom: '1px solid #1e2539', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0 }} />
        <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 13 }}>Watchlist</span>
        <span style={{ color: '#5b6678', fontSize: 11, marginLeft: 'auto' }}>{universe.length}</span>
        {asDrawer && (
          <button onClick={onClose} aria-label="Schließen" style={{
            background: 'transparent', border: 'none', color: '#8b93a7', fontSize: 20,
            cursor: 'pointer', padding: 0, width: 32, height: 32, lineHeight: 1,
          }}>✕</button>
        )}
      </div>
      <input
        value={q} onChange={e => setQ(e.target.value)} placeholder="Suche…"
        style={{ margin: 10, background: '#0a0e1a', color: '#fff', border: '1px solid #1e2539', borderRadius: 7, padding: '7px 10px', fontSize: 12 }}
      />
      <div style={{ overflowY: 'auto', flex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 0, padding: '0 12px 6px', fontSize: 9, color: '#475569', textTransform: 'uppercase' }}>
          <span>Symbol</span><span style={{ textAlign: 'right' }}>Preis</span><span style={{ textAlign: 'right', paddingLeft: 10 }}>24h</span>
        </div>
        {rows.map(u => {
          const t = tickers[u.symbol];
          const chg = t?.changePct ?? 0;
          const isSel = u.symbol === selected;
          return (
            <div key={u.symbol} onClick={() => pick(u.symbol)}
              style={{
                display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center',
                padding: '10px 12px', cursor: 'pointer', borderLeft: '2px solid ' + (isSel ? '#8b5cf6' : 'transparent'),
                background: isSel ? '#161d33' : 'transparent',
              }}
              onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#11172a'; }}
              onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ overflow: 'hidden' }}>
                <div style={{ color: '#e2e8f0', fontSize: 12, fontWeight: 600 }}>{u.base}</div>
                <div style={{ color: '#475569', fontSize: 9, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{u.name}</div>
              </div>
              <div style={{ textAlign: 'right', color: '#cbd5e1', fontSize: 12, fontFamily: 'monospace' }}>
                {t ? fmtPrice(t.last, u.tick_size) : '–'}
              </div>
              <div style={{ textAlign: 'right', paddingLeft: 10, fontSize: 11, fontFamily: 'monospace', color: chg >= 0 ? '#22c55e' : '#ef4444' }}>
                {chg >= 0 ? '+' : ''}{chg.toFixed(2)}%
              </div>
            </div>
          );
        })}
        {!rows.length && <div style={{ padding: 20, color: '#475569', fontSize: 12, textAlign: 'center' }}>Keine Treffer</div>}
      </div>
      </div>
    </>
  );
};

export default Watchlist;
